import { WintSuperkollAPIModel, WintSuperkollAPIModelDeadlines, WintUtilsWebApi } from "wint-api-client/dist/types";
import { AuthEnums, CurrencyEnums, StyleEnums } from "models/enums";
import { DateHelper, LocalizationHelperInstance, EnumHelper, InvoiceHelper } from "utils/helpers";
import { DateConstants, IconDefinitionConstants, ReportConstants, ServiceConstants } from "utils/constants";
import useAPIClientHelper from "./useAPIClientHelper";
import {
  ReceiptService,
  SupportService,
  BankPaymentInformationService,
  StoppedPaymentsService,
  TodoService,
} from "services";
import usePaymentSigningInformationService from "./usePaymentSigningInformationService";
import useSalaryDeviationsService from "./useSalaryDeviationsService";
import usePersonSalaryService from "./usePersonSalaryService";
import useSupplementaryMaterialService from "./useSupplementaryMaterialService";
import useProfileRoutineService from "./useProfileRoutineService";
import { AllTodoListItemType, TodoChipInformation, TodoExtended } from "models/propTypes/todoPropTypes";
import { AxiosResponse } from "axios";
import { User, Supplier, Customer } from "models/propTypes/userProfilePropTypes";
import { TranslationKeys } from "translations";
import { TodoFormTypes } from "models/formTypes";
import { CurrencyCode } from "models/enums/currency";
import { formatRevenueReportingPeriod } from "components/revenueReporting/revenueReportingPeriod";
import useSalaryPermissions from "components/salary/hooks/useSalaryPermissions";

function useTodoService() {
  const { apiClientInstance } = useAPIClientHelper();

  const paymentSigningInformationService = usePaymentSigningInformationService();
  const salaryDeviationsService = useSalaryDeviationsService();
  const supplementaryMaterialService = useSupplementaryMaterialService();
  const profileRoutineService = useProfileRoutineService();
  const personSalaryService = usePersonSalaryService();
  const { isWintSalaryCompany } = useSalaryPermissions();

  const snooze = (id: number, type: WintSuperkollAPIModel.TodoType, snoozeUntil: Date) => {
    return apiClientInstance(AuthEnums.AuthType.Company).TodoClient.SnoozeByIdasinteger(id, {
      type,
      snoozeUntilUtc: DateHelper.getUtcDate(snoozeUntil),
    });
  };

  const snoozeByIdAsGuid = (id: string, type: WintSuperkollAPIModel.TodoType, snoozeUntil: Date) => {
    return apiClientInstance(AuthEnums.AuthType.Company).TodoClient.SnoozeByIdasguid(id, {
      type,
      snoozeUntilUtc: DateHelper.getUtcDate(snoozeUntil),
    });
  };

  const getTodoIconByType = (type: WintSuperkollAPIModel.TodoType) => {
    switch (type) {
      case WintSuperkollAPIModel.TodoType.IncomingInvoice:
        return IconDefinitionConstants.WEnvelop;
      case WintSuperkollAPIModel.TodoType.Receipt:
        return IconDefinitionConstants.WCosts;
      case WintSuperkollAPIModel.TodoType.MissingDocument:
        return IconDefinitionConstants.WQuestionCircle;
      case WintSuperkollAPIModel.TodoType.Support:
        return IconDefinitionConstants.WCommentDots;
      case WintSuperkollAPIModel.TodoType.DocumentDraft:
        return IconDefinitionConstants.WDownload;
      case WintSuperkollAPIModel.TodoType.ProfileRoutine:
        return IconDefinitionConstants.WRecurring;
      case WintSuperkollAPIModel.TodoType.CustomerFeedback:
        return IconDefinitionConstants.WCommentDots;
      case WintSuperkollAPIModel.TodoType.PaymentSigningInformation:
      case WintSuperkollAPIModel.TodoType.RevenueReportNotSubmitted:
        return IconDefinitionConstants.WMoney;
      case WintSuperkollAPIModel.TodoType.SalaryDraft:
      case WintSuperkollAPIModel.TodoType.SalaryDeviationReport:
      case WintSuperkollAPIModel.TodoType.PersonSalaryReport:
      case WintSuperkollAPIModel.TodoType.UpdateNewBoardMembersData:
      case WintSuperkollAPIModel.TodoType.WintSalaryApprovalReport:
        return IconDefinitionConstants.WUser;
      case WintSuperkollAPIModel.TodoType.Invoice:
        return IconDefinitionConstants.WPaperPlane;
      case WintSuperkollAPIModel.TodoType.AgentSaleMissingInformation:
        return IconDefinitionConstants.WLink;
      case WintSuperkollAPIModel.TodoType.Lead:
        return IconDefinitionConstants.WEmpty;
      case WintSuperkollAPIModel.TodoType.QualifiedShareDividend:
      case WintSuperkollAPIModel.TodoType.AnnualAccountsForCustomerApproval:
      case WintSuperkollAPIModel.TodoType.ShareRegister:
      case WintSuperkollAPIModel.TodoType.AnnualReportSigningSteps:
      case WintSuperkollAPIModel.TodoType.AuditorAnnualReportSigningSteps:
      case WintSuperkollAPIModel.TodoType.PreliminaryTaxUpdate:
        return IconDefinitionConstants.WCalculator;
      case WintSuperkollAPIModel.TodoType.GenericVouchersSentBack:
      case WintSuperkollAPIModel.TodoType.RemovedDocumentDraft:
        return IconDefinitionConstants.WDocument;
      case WintSuperkollAPIModel.TodoType.Survey:
        return IconDefinitionConstants.WQuestionCircle;
      case WintSuperkollAPIModel.TodoType.ClassifiedReceiptTodo:
        return IconDefinitionConstants.WCosts;
    }
  };

  const getTodoSubTypeByType = (item: WintSuperkollAPIModel.Todo) => {
    switch (item.type) {
      case WintSuperkollAPIModel.TodoType.SupplementaryMaterial:
        const todoType = supplementaryMaterialService.getTodoType(item.supplementaryMaterials?.sourceType);
        return LocalizationHelperInstance.getFormattedMessage(`Common.TodoType_${todoType}`);
      default:
        return undefined;
    }
  };

  const reportToAPIFilter = (
    formFilter: TodoFormTypes.Filter
  ): Omit<WintSuperkollAPIModel.TodoFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      isSnoozed: formFilter.snoozedOnly,
      types: TodoService.getTodoTypesForReportFilter(),
      urgency:
        formFilter.urgency !== undefined
          ? formFilter.urgency.map((u) => u.id as WintSuperkollAPIModel.TodoUrgency)
          : undefined,
      groups:
        formFilter.groups !== undefined
          ? formFilter?.groups?.map((g) => g.id as WintSuperkollAPIModel.TodoGroup)
          : undefined,
      subTypes: formFilter.subTypes?.flatMap((subType) => {
        return typeof subType.id === "string"
          ? subType.id.split(",").map((i) => Number(i) as WintSuperkollAPIModel.TodoSubType)
          : (subType.id as WintSuperkollAPIModel.TodoSubType);
      }),
      deadlineTypes:
        formFilter.deadlineTypes !== undefined
          ? formFilter?.deadlineTypes?.map((g) => g.id as WintSuperkollAPIModelDeadlines.DeadlineType)
          : undefined,
    };
  };

  const reportFilter = (baseFilter: WintUtilsWebApi.Filter, key?: number, formFilter?: TodoFormTypes.Filter) => {
    const filter = reportToAPIFilter(formFilter ?? {});

    const apiResults = apiClientInstance(AuthEnums.AuthType.Company).TodoClient.Get({ ...baseFilter, ...filter });
    let response: AxiosResponse<WintUtilsWebApi.ListResponse<WintSuperkollAPIModel.Todo>> = {} as AxiosResponse<
      WintUtilsWebApi.ListResponse<WintSuperkollAPIModel.Todo>
    >;
    return apiResults
      .then((res) => {
        response = res;
        return res.data;
      })
      .then((result) => {
        const items = apiToReport(result);
        response.data = items;
        return { ...response, key: key };
      });
  };

  const apiToReport = (
    response: WintUtilsWebApi.ListResponse<WintSuperkollAPIModel.Todo>
  ): WintUtilsWebApi.ListResponse<AllTodoListItemType> => {
    const items = response.items?.map(
      (item): AllTodoListItemType => ({
        ...item,
        ...mapToListItem(item),
      })
    );

    return {
      ...response,
      items: items,
    };
  };

  const mapToListItem = (
    item: WintSuperkollAPIModel.Todo
  ): Omit<AllTodoListItemType, keyof WintSuperkollAPIModel.Todo> => {
    const today = new Date();

    switch (item.type) {
      case WintSuperkollAPIModel.TodoType.IncomingInvoice:
        return {
          counterpartMapping: new Supplier(item.counterpartId, item.counterpartName),
          informationMapping: `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.No)}. ${
            item.information
          }`,
          currency: item.incomingInvoice?.currency,
        };
      case WintSuperkollAPIModel.TodoType.Receipt:
      case WintSuperkollAPIModel.TodoType.ReceiptDraft:
        return {
          currency: item.receipt?.currency,
        };
      case WintSuperkollAPIModel.TodoType.Support:
        return {
          states:
            item.supportIssue?.state !== undefined ? SupportService.getStateText(item.supportIssue.state) : undefined,
        };
      case WintSuperkollAPIModel.TodoType.DocumentDraft:
        return {
          counterpartMapping: new User(item.counterpartId ?? 0, item.counterpartName),
          states: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Todo2),
          currency: item.documentDraft?.currency,
        };
      case WintSuperkollAPIModel.TodoType.ProfileRoutine:
        return {
          states:
            item.profileRoutine?.state !== undefined
              ? LocalizationHelperInstance.getFormattedMessage(`ProfileRoutine.State_${item.profileRoutine?.state}`)
              : undefined,
        };
      case WintSuperkollAPIModel.TodoType.SalaryDeviationReport:
        return {
          informationMapping:
            item.information !== undefined
              ? DateHelper.formatAPIYearMonthToNumericString(DateHelper.splitYearMonth(Number(item.information)))
              : undefined,
        };
      case WintSuperkollAPIModel.TodoType.Invoice:
        return {
          counterpartMapping: new Customer(item.counterpartId ?? 0, item.counterpartName),
          informationMapping:
            item.information !== undefined
              ? `${LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.Invoice.No
                )}. ${InvoiceHelper.formatInvoiceNumber(Number(item.information))}`
              : "-",
          currency: item.invoice?.currency,
        };
      case WintSuperkollAPIModel.TodoType.SelfInvoice:
        return {
          counterpartMapping:
            item.counterpartId !== undefined ? new Customer(item.counterpartId, item.counterpartName) : undefined,
          informationMapping:
            item.information !== undefined
              ? `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.No)}. ${item.information}`
              : "-",
          currency: item.selfInvoice?.currency ?? CurrencyEnums.CurrencyCode.SEK,
        };
      case WintSuperkollAPIModel.TodoType.AgentSaleMissingInformation:
        return {
          counterpartMapping: new User(item.counterpartId ?? 0, item.counterpartName),
        };
      case WintSuperkollAPIModel.TodoType.PersonSalaryReport:
        return {
          counterpartMapping: new User(),
          informationMapping:
            item.personSalaryReport !== undefined
              ? DateHelper.formatAPIYearMonthToNumericString(DateHelper.getYearMonthByDate(today))
              : undefined,
        };
      case WintSuperkollAPIModel.TodoType.SalaryDeviationAndTimeLogReport:
      case WintSuperkollAPIModel.TodoType.SalaryDeviationReportWithBusinessUnits:
        return {
          informationMapping:
            item.information !== undefined
              ? DateHelper.formatAPIYearMonthToNumericString(DateHelper.splitYearMonth(Number(item.information)))
              : undefined,
        };
      case WintSuperkollAPIModel.TodoType.AnnualAccountsForCustomerApproval:
        return {
          informationMapping: DateHelper.getYear(new Date(item.information ?? item.date)).toString(),
        };
      case WintSuperkollAPIModel.TodoType.RemovedDocumentDraft:
        return {
          counterpartMapping: new User(0, item.counterpartName),
          informationMapping: item.information,
          states: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Todo2),
          currency: item.documentDraft?.currency,
        };
      case WintSuperkollAPIModel.TodoType.WintSalaryApprovalReport:
      case WintSuperkollAPIModel.TodoType.SalaryDraft:
      case WintSuperkollAPIModel.TodoType.QualifiedShareDividend:
      case WintSuperkollAPIModel.TodoType.GenericVouchersSentBack:
      case WintSuperkollAPIModel.TodoType.SupplementaryMaterial:
      case WintSuperkollAPIModel.TodoType.MissingDocument:
      case WintSuperkollAPIModel.TodoType.CustomerFeedback:
      case WintSuperkollAPIModel.TodoType.PaymentSigningInformation:
      case WintSuperkollAPIModel.TodoType.Survey:
      case WintSuperkollAPIModel.TodoType.UnhandledBankPayments:
      case WintSuperkollAPIModel.TodoType.StoppedPayment:
      case WintSuperkollAPIModel.TodoType.PausedPayments:
      case WintSuperkollAPIModel.TodoType.PreliminaryTaxUpdate:
        return {};
      default:
        return {
          states: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Todo),
        };
    }
  };

  const getGroupTypeText = (group: WintSuperkollAPIModel.TodoGroup) => {
    return LocalizationHelperInstance.getTranslatedEnumValue(
      "Todos.TodoGroup",
      WintSuperkollAPIModel.TodoGroup[group].toString()
    );
  };

  const getTodoCardSubTypeName = (type: WintSuperkollAPIModel.TodoSubType) =>
    LocalizationHelperInstance.getTranslatedEnumValue(
      "Todos.TodoCardSubTypeText",
      WintSuperkollAPIModel.TodoSubType[type].toString()
    );

  const getTodoCardNameKey = (type: WintSuperkollAPIModel.TodoSubType) =>
    type === WintSuperkollAPIModel.TodoSubType.SalaryDeviationAndTimeLogReport && isWintSalaryCompany
      ? TranslationKeys.Todos.TodoCardName_WintSalaryTimeLogReport
      : `Todos.TodoCardName_${WintSuperkollAPIModel.TodoSubType[type].toString()}`;

  const getTodoCardSubTypeFilterNames = (type: WintSuperkollAPIModel.TodoSubType) => {
    switch (type) {
      case WintSuperkollAPIModel.TodoSubType.SalaryDraft:
      case WintSuperkollAPIModel.TodoSubType.SalaryDeviationReport:
      case WintSuperkollAPIModel.TodoSubType.PersonSalaryReport:
      case WintSuperkollAPIModel.TodoSubType.SalaryDeviationAndTimeLogReport:
      case WintSuperkollAPIModel.TodoSubType.SalaryDeviationReportWithBusinessUnits:
      case WintSuperkollAPIModel.TodoSubType.WintSalaryApprovalReport:
        return LocalizationHelperInstance.getFormattedMessage(getTodoCardNameKey(type));
      default:
        return LocalizationHelperInstance.getTranslatedEnumValue(
          "Todos.TodoCardSubTypeText",
          WintSuperkollAPIModel.TodoSubType[type].toString()
        );
    }
  };

  const getTodoCardName = (group: WintSuperkollAPIModel.TodoGroup, type: WintSuperkollAPIModel.TodoSubType) => {
    if (type === WintSuperkollAPIModel.TodoSubType.AgentSaleMissingInformation) {
      return getGroupTypeText(group);
    }
    return LocalizationHelperInstance.getFormattedMessage(getTodoCardNameKey(type));
  };

  const getTodoCardNameWithSubType = (item: WintSuperkollAPIModel.Todo) => {
    const todoCardName =
      item.subType === WintSuperkollAPIModel.TodoSubType.SalaryDeviationAndTimeLogReport
        ? getTodoCardName(item.group, item.subType)
        : TodoService.getTodoHeaderText(item);
    const subTypeName = LocalizationHelperInstance.getTranslatedEnumValue(
      "Todos.TodoCardSubTypeText",
      WintSuperkollAPIModel.TodoSubType[item.subType].toString()
    );

    return `${todoCardName}${subTypeName.trim() !== "" ? ` - ${subTypeName}` : ""}`;
  };

  const mapToChip = (item: TodoExtended): TodoChipInformation => {
    const today = new Date();
    const chipBaseInfo: TodoChipInformation = {
      title: getTodoCardNameWithSubType(item),
      urgency: item.urgencyChanged ? undefined : item.urgency,
    };

    switch (item.type) {
      case WintSuperkollAPIModel.TodoType.IncomingInvoice:
        return {
          ...chipBaseInfo,
          subText: item.incomingInvoice?.supplier?.name,
          primaryInfo:
            item.incomingInvoice?.amountWithSign !== undefined
              ? LocalizationHelperInstance.getFormattedCurrency(
                  item.incomingInvoice.amountWithSign,
                  item.incomingInvoice.currency
                )
              : "",
          date: item.incomingInvoice!.dueDate,
        };
      case WintSuperkollAPIModel.TodoType.Receipt:
      case WintSuperkollAPIModel.TodoType.ReceiptDraft:
        return {
          ...chipBaseInfo,
          subText: `${item.receipt?.createdByPersonName}${
            item.subType !== WintSuperkollAPIModel.TodoSubType.ReceiptSentBack && item.receipt?.receiptCategoryId
              ? ` - ${ReceiptService.findReceiptCategoryByCategoryId(item.receipt?.receiptCategoryId)?.name}`
              : ""
          }`,
          primaryInfo:
            item.receipt?.amount !== undefined
              ? LocalizationHelperInstance.getFormattedCurrency(item.receipt.amount, item.receipt?.currency)
              : "",
          date:
            item.subType === WintSuperkollAPIModel.TodoSubType.ReceiptSentBack
              ? (item.receipt?.sendBackData?.date ?? item.receipt?.dateTime)
              : item.receipt?.dateTime,
        };
      case WintSuperkollAPIModel.TodoType.ClassifiedReceiptTodo:
        return {
          ...chipBaseInfo,
          subText: item.classifiedReceipt?.createdByPersonName,
          date: item.classifiedReceipt?.created,
        };
      case WintSuperkollAPIModel.TodoType.MissingDocument:
        return {
          ...chipBaseInfo,
          subText: item.missingDocument?.text,
          primaryInfo:
            item.missingDocument?.amount !== undefined
              ? LocalizationHelperInstance.getFormattedCurrency(
                  item.missingDocument.amount,
                  CurrencyEnums.CurrencyCode.SEK
                )
              : "",
          date: item.missingDocument?.date,
        };
      case WintSuperkollAPIModel.TodoType.Support:
        return {
          ...chipBaseInfo,
          subText: item.supportIssue?.title,
          date: item.supportIssue ? SupportService.getLatestCommentDate(item.supportIssue) : undefined,
        };
      case WintSuperkollAPIModel.TodoType.DocumentDraft:
        return {
          ...chipBaseInfo,
          subText: item.documentDraft?.subject,
          primaryInfo: item.documentDraft?.from,
          date: item.documentDraft?.created,
        };
      case WintSuperkollAPIModel.TodoType.ProfileRoutine:
        return {
          ...chipBaseInfo,
          subText: item.profileRoutine?.name,
          primaryInfo: profileRoutineService.getFrequencyName(item.profileRoutine?.frequency),
          date: item.profileRoutine?.routineDate
            ? DateHelper.addDays(item.profileRoutine.routineDate, DateConstants.defaultProfileRoutineDueDateDiff)
            : undefined,
        };
      case WintSuperkollAPIModel.TodoType.PaymentSigningInformation:
        return {
          ...chipBaseInfo,
          subText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Payments.PaymentsCount, "", {
            num: paymentSigningInformationService.getNumOfPayments(item.paymentSigningInformation).toString(),
          }),
          primaryInfo: LocalizationHelperInstance.getFormattedCurrency(
            item.paymentSigningInformation !== undefined
              ? paymentSigningInformationService.getPayout(item.paymentSigningInformation)
              : 0,
            CurrencyEnums.CurrencyCode.SEK
          ),
          date:
            item.paymentSigningInformation !== undefined
              ? paymentSigningInformationService.getDueDate(item.paymentSigningInformation)
              : undefined,
        };
      case WintSuperkollAPIModel.TodoType.SalaryDraft:
        return {
          ...chipBaseInfo,
          subText:
            item.salaryDraft !== undefined
              ? LocalizationHelperInstance.getFormattedDate(
                  DateHelper.getDateFromYearMonth(item.salaryDraft.yearAndMonth),
                  DateConstants.MonthYearNumericConfig
                )
              : undefined,
          date: DateHelper.getFirstDayOfNextMonth(today),
        };
      case WintSuperkollAPIModel.TodoType.SalaryDeviationReport:
        return {
          ...chipBaseInfo,
          subText:
            item.salaryDeviationReport?.yearMonth !== undefined
              ? LocalizationHelperInstance.getFormattedDate(
                  DateHelper.getDateFromYearMonth(item.salaryDeviationReport.yearMonth),
                  DateConstants.MonthYearNumericConfig
                )
              : "",
          date: item.salaryDeviationReport?.yearMonth
            ? DateHelper.addDays(
                DateHelper.getFirstDayOfNextMonth(
                  DateHelper.getDateFromYearMonth(item.salaryDeviationReport.yearMonth)
                ),
                4
              )
            : undefined,
        };
      case WintSuperkollAPIModel.TodoType.Invoice:
        return {
          ...chipBaseInfo,
          subText: item.invoice?.customerName,
          primaryInfo:
            (item.subType === WintSuperkollAPIModel.TodoSubType.InvoiceOverdue ||
              item.subType === WintSuperkollAPIModel.TodoSubType.InvoiceReminderFeeNotPaid) &&
            item.invoice?.leftToPay !== undefined
              ? LocalizationHelperInstance.getFormattedCurrency(item.invoice.leftToPay, item.invoice?.currency)
              : item.invoice?.totalAmount !== undefined
                ? LocalizationHelperInstance.getFormattedCurrency(item.invoice.totalAmount, item.invoice?.currency)
                : "",
          date:
            item.subType === WintSuperkollAPIModel.TodoSubType.InvoiceDraft
              ? item.invoice?.postingDate
              : (item.invoice?.reminderDueDate ?? item.invoice?.dueDate),
        };
      case WintSuperkollAPIModel.TodoType.AgentSaleMissingInformation:
        return {
          ...chipBaseInfo,
          subText: item.agentSaleMissingInformation?.missingInformationTexts?.reduce((p, c, i, a) => `${p}\n${c}`, ""),
          primaryInfo: item.agentSaleMissingInformation?.objectNumber,
          date: item.date,
        };
      case WintSuperkollAPIModel.TodoType.PersonSalaryReport:
        return {
          ...chipBaseInfo,
          subText: LocalizationHelperInstance.getFormattedDate(today, DateConstants.MonthYearNumericConfig),
          primaryInfo: LocalizationHelperInstance.getFormattedCurrency(
            personSalaryService.getAmount(item.personSalaryReport),
            CurrencyEnums.CurrencyCode.SEK
          ),
          date: DateHelper.getFirstDayOfNextMonth(today),
        };
      case WintSuperkollAPIModel.TodoType.UnhandledBankPayments:
        return {
          ...chipBaseInfo,
          subText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Payments.PaymentsCount, "", {
            num: BankPaymentInformationService.getNumOfPayments(item.unhandledBankPayments).toString(),
          }),
          primaryInfo: LocalizationHelperInstance.getFormattedCurrency(
            BankPaymentInformationService.getPayout(item.unhandledBankPayments),
            CurrencyEnums.CurrencyCode.SEK
          ),
          date:
            item.unhandledBankPayments !== undefined
              ? BankPaymentInformationService.getDueDate(item.unhandledBankPayments)
              : undefined,
        };
      case WintSuperkollAPIModel.TodoType.StoppedPayment:
        return {
          ...chipBaseInfo,
          subText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Payments.PaymentsCount, "", {
            num: `${item.stoppedPayments?.stoppedPaymentList?.length ?? 0}`,
          }),
          primaryInfo: LocalizationHelperInstance.getFormattedCurrency(
            item.stoppedPayments?.amount ?? 0,
            CurrencyEnums.CurrencyCode.SEK
          ),
          date: StoppedPaymentsService.getDueDate(item.stoppedPayments),
        };
      case WintSuperkollAPIModel.TodoType.PausedPayments:
        return {
          ...chipBaseInfo,
          subText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Payments.PaymentsCount, "", {
            num: `${item.pausedPayments?.pausedPaymentList?.length ?? 0}`,
          }),
          primaryInfo: LocalizationHelperInstance.getFormattedCurrency(
            item.pausedPayments?.amount ?? 0,
            CurrencyEnums.CurrencyCode.SEK
          ),
          date: item.pausedPayments?.date,
        };
      case WintSuperkollAPIModel.TodoType.QualifiedShareDividend:
        return {
          ...chipBaseInfo,
          subText: item.qualifiedShareDividend?.incomeYear,
          primaryInfo:
            item.qualifiedShareDividend?.dividend !== undefined
              ? LocalizationHelperInstance.getFormattedCurrency(
                  item.qualifiedShareDividend.dividend,
                  CurrencyEnums.CurrencyCode.SEK
                )
              : "",
          date: item.qualifiedShareDividend!.dueDate,
        };
      case WintSuperkollAPIModel.TodoType.GenericVouchersSentBack:
        return {
          ...chipBaseInfo,
          subText: item.genericVoucherSendBack?.description,
          primaryInfo:
            item.genericVoucherSendBack?.amount !== undefined
              ? LocalizationHelperInstance.getFormattedCurrency(
                  item.genericVoucherSendBack.amount,
                  CurrencyEnums.CurrencyCode.SEK
                )
              : "",
          date: item.genericVoucherSendBack!.updatedDate,
        };
      case WintSuperkollAPIModel.TodoType.SupplementaryMaterial:
        return {
          ...chipBaseInfo,
          subText: item.supplementaryMaterials?.reason,
          primaryInfo:
            item.supplementaryMaterials?.sourceValue !== undefined
              ? LocalizationHelperInstance.getFormattedCurrency(
                  item.supplementaryMaterials.sourceValue,
                  CurrencyEnums.CurrencyCode.SEK
                )
              : "",
          date: item.supplementaryMaterials?.updated,
        };
      case WintSuperkollAPIModel.TodoType.Survey:
        return {
          ...chipBaseInfo,
          date: item.surveyStatus?.lastDateToAnswer,
        };
      case WintSuperkollAPIModel.TodoType.SalaryDeviationAndTimeLogReport:
      case WintSuperkollAPIModel.TodoType.SalaryDeviationReportWithBusinessUnits:
        return {
          ...chipBaseInfo,
          subText:
            item.salaryDeviationAndTimeLogReport?.yearAndMonth !== undefined
              ? DateHelper.formatAPIYearMonthToNumericString(
                  DateHelper.splitYearMonth(item.salaryDeviationAndTimeLogReport.yearAndMonth)
                )
              : undefined,
          date:
            item.salaryDeviationAndTimeLogReport?.yearAndMonth !== undefined
              ? salaryDeviationsService.getDueDateForTimeLog(item.salaryDeviationAndTimeLogReport.yearAndMonth)
              : undefined,
        };
      case WintSuperkollAPIModel.TodoType.AnnualAccountsForCustomerApproval:
        return {
          ...chipBaseInfo,
          subText: DateHelper.getYear(
            new Date(item.annualReportForCustomerApproval?.financialYear?.end ?? item.date)
          ).toString(),
          primaryInfo: " ",
          date: item.annualReportForCustomerApproval?.financialYear?.end,
        };
      case WintSuperkollAPIModel.TodoType.UpdateNewBoardMembersData:
        return {
          ...chipBaseInfo,
          subText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.BoardMember.AddInformation),
        };
      case WintSuperkollAPIModel.TodoType.ShareRegister:
        return {
          ...chipBaseInfo,
          date: item.date,
        };
      case WintSuperkollAPIModel.TodoType.PreliminaryTaxUpdate:
        return {
          ...chipBaseInfo,
          primaryInfo:
            item.preliminaryTaxUpdate?.finalTaxToPay !== undefined
              ? LocalizationHelperInstance.getFormattedCurrency(
                  item.preliminaryTaxUpdate.finalTaxToPay,
                  CurrencyEnums.CurrencyCode.SEK
                )
              : "",
          date: item.preliminaryTaxUpdate?.customerDeadline,
        };
      case WintSuperkollAPIModel.TodoType.AgingIncomingInvoice:
        return {
          ...chipBaseInfo,
          subText: item.agingIncomingInvoiceTodoItem?.supplier?.name,
          primaryInfo:
            item.agingIncomingInvoiceTodoItem?.amountWithSign !== undefined
              ? LocalizationHelperInstance.getFormattedCurrency(
                  item.agingIncomingInvoiceTodoItem.amountWithSign,
                  item.agingIncomingInvoiceTodoItem.currency
                )
              : "",
          date: item.agingIncomingInvoiceTodoItem!.dueDate,
        };
      case WintSuperkollAPIModel.TodoType.SelfInvoice:
        return {
          ...chipBaseInfo,
          subText: item.selfInvoice?.customerName ?? undefined,
          primaryInfo:
            item.selfInvoice?.totalAmount !== undefined
              ? LocalizationHelperInstance.getFormattedCurrency(
                  item.selfInvoice.totalAmount,
                  item.selfInvoice.currency ?? CurrencyCode.SEK
                )
              : "",
          date: item.selfInvoice?.dueDate,
        };
      case WintSuperkollAPIModel.TodoType.WintSalaryApprovalReport:
        return {
          ...chipBaseInfo,
          subText: item.information,
          date: item.date,
        };
      case WintSuperkollAPIModel.TodoType.RemovedDocumentDraft:
        return {
          ...chipBaseInfo,
          subText: `${item.documentDraft?.from} - ${(item.documentDraft?.attachments ?? [])[0]?.fileName ?? item.documentDraft?.subject ?? ""}`,
          date: item.documentDraft?.created,
        };
      case WintSuperkollAPIModel.TodoType.RevenueReportNotSubmitted: {
        if (item.revenueReportNotSubmitted === undefined) {
          return chipBaseInfo;
        }
        const revenueReportPeriod = formatRevenueReportingPeriod(item.revenueReportNotSubmitted);
        const revenueReportTemplateName = item.revenueReportNotSubmitted.templateName?.trim();
        return {
          ...chipBaseInfo,
          subText: revenueReportTemplateName
            ? `${revenueReportTemplateName} - ${revenueReportPeriod}`
            : revenueReportPeriod,
        };
      }
      default:
        return chipBaseInfo;
    }
  };

  const getTodoTypeText = (type: WintSuperkollAPIModel.TodoType) => {
    return LocalizationHelperInstance.getFormattedMessage(`Common.TodoType_${type}`);
  };

  const getUrgencyTypeText = (urgency: WintSuperkollAPIModel.TodoUrgency) => {
    return LocalizationHelperInstance.getFormattedMessage(`Todos.Urgency_${urgency}`);
  };

  const getUIDisplayTypeByUrgency = (urgency?: WintSuperkollAPIModel.TodoUrgency) => {
    if (urgency === undefined) return StyleEnums.DisplayType.None;

    switch (urgency) {
      case WintSuperkollAPIModel.TodoUrgency.Overdue:
        return StyleEnums.DisplayType.Danger;
      case WintSuperkollAPIModel.TodoUrgency.Urgent:
        return StyleEnums.DisplayType.Warning;
      default:
        return StyleEnums.DisplayType.Info;
    }
  };

  const mapTodoTypeToReportKey = (todoType: WintSuperkollAPIModel.TodoType) => {
    const mapping = Object.entries(ReportConstants.TodoTypeToReportKey).find(
      ([key, value]) => key === todoType.toString()
    );
    if (mapping) {
      return mapping[1];
    } else {
      return undefined;
    }
  };

  const getDeadlineTypeName = (type: WintSuperkollAPIModelDeadlines.DeadlineType) => {
    return LocalizationHelperInstance.getTranslatedEnumValue("Todos.DeadlineType", type);
  };

  const getDefaultDeadlineTypes = () => {
    return EnumHelper.getNumericItemValues(WintSuperkollAPIModelDeadlines.DeadlineType)
      .filter((type) => type !== WintSuperkollAPIModelDeadlines.DeadlineType.Unknown)
      .map((type) => ({
        id: type,
        value: getDeadlineTypeName(type),
      }));
  };

  const getDeadlines = (types?: WintSuperkollAPIModelDeadlines.DeadlineType[]) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .DeadlineClient.GetByFilter({ ...ServiceConstants.DefaultFilter, types })
      .then((res) => res.data);
  };

  const getMostUrgentDeadline = () => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .DeadlineClient.GetMostUrgent()
      .then((res) => res.data);
  };

  const getDeadlineById = async (deadlineId: number) => {
    const res = await apiClientInstance(AuthEnums.AuthType.Company).DeadlineClient.GetById(deadlineId);
    return res.data;
  };

  return {
    snooze,
    snoozeByIdAsGuid,
    getTodoIconByType,
    getTodoSubTypeByType,
    reportToAPIFilter,
    reportFilter,
    apiToReport,
    mapToListItem,
    getGroupTypeText,
    getTodoCardSubTypeName,
    getTodoCardSubTypeFilterNames,
    getTodoCardName,
    getTodoCardNameWithSubType,
    mapToChip,
    getTodoTypeText,
    getUrgencyTypeText,
    getUIDisplayTypeByUrgency,
    mapTodoTypeToReportKey,
    getDeadlineTypeName,
    getDeadlines,
    getDefaultDeadlineTypes,
    getMostUrgentDeadline,
    getDeadlineById,
  };
}

export default useTodoService;
